'use client';

import React, { useRef } from 'react';
import { isFilled, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import SplashButton from '@/components/SplashButton';
import { ModalVideoPlayer } from '@/components/ModalVideoPlayer';

export interface VideoSectionProps {
  backgroundVideoUrl?: string;
  title?: RichTextField;
  description?: RichTextField;
  ctaText?: string;
  videoId?: string;
  videoTitle?: string;
  videoLocation?: string;
  className?: string;
}

export function VideoSection({
  backgroundVideoUrl,
  title,
  description,
  ctaText,
  videoId,
  videoTitle,
  videoLocation = 'VideoSection',
  className,
}: VideoSectionProps) {
  const showCta = Boolean(videoId && ctaText);

  const cardRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'center center'],
  });
  const maxWidth = useTransform(scrollYProgress, [0, 1], [1000, 1100]);

  return (
    <section
      className={['w-full px-4 py-16 sm:py-24 pt-0 sm:pt-0', className].filter(Boolean).join(' ')}
    >
      <motion.div
        ref={cardRef}
        style={{ maxWidth }}
        className="relative mx-auto flex min-h-[500px] w-full items-end overflow-hidden rounded-2xl bg-black shadow-xl shadow-black/30 sm:min-h-[600px]"
      >
        {backgroundVideoUrl && (
          <video
            className="absolute inset-0 z-0 h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          >
            <source src={backgroundVideoUrl} type="video/mp4" />
          </video>
        )}

        <div className="absolute inset-0 z-10 bg-blue-900/70" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-2/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          className="relative z-20 flex w-full flex-col gap-8 px-6 py-10 text-white sm:flex-row sm:items-end sm:justify-between sm:gap-12 sm:px-12 sm:py-12"
        >
          <div className="max-w-2xl text-left">
            {title && isFilled.richText(title) && (
              <h2 className="text-3xl font-light leading-tight sm:text-4xl md:text-5xl">
                <PrismicRichText
                  field={title}
                  components={{
                    paragraph: ({ children }) => <>{children}</>,
                    strong: ({ children }) => <strong>{children}</strong>,
                  }}
                />
              </h2>
            )}

            {description && isFilled.richText(description) && (
              <div className="mt-4 text-base text-neutral-200">
                <PrismicRichText field={description} />
              </div>
            )}
          </div>

          {showCta && (
            <div className="shrink-0">
              <ModalVideoPlayer
                videoId={videoId as string}
                videoTitle={videoTitle || (ctaText as string)}
                videoLocation={videoLocation}
              >
                <SplashButton
                  url="#"
                  color="red"
                  size="md"
                  icon="video"
                  text={ctaText as string}
                  className="ph-convert min-w-[260px]"
                />
              </ModalVideoPlayer>
            </div>
          )}
        </motion.div>
      </motion.div>
    </section>
  );
}

export default VideoSection;
